import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { selectActiveDsTab } from 'redux/mode/reducers';
import { DesignSystemComponent, ComponentCategory } from 'designSystem/types';
import { buildTabId } from 'designSystem/util';
import { addDsTab, setActiveDsTab, setDsViewPreference } from 'redux/mode/actions';
import { IconType } from 'components/Icon';
import ViewToggle from 'components/Widget/ViewToggle';
import ComponentCard from './ComponentCard';
import ComponentPage from './ComponentPage';
import FilterPills from '../FilterPills';
import useScrollMemory from 'hooks/useScrollMemory';
import * as Base from '../styles';
import * as S from './styles';

// Map ComponentCategory to display name
const categoryDisplayNames: Record<ComponentCategory, string> = {
	button: 'Buttons',
	card: 'Cards',
	form: 'Forms',
	navigation: 'Navigation',
	'data-display': 'Data Display',
	other: 'Other',
};

const ComponentsView = () => {
	const dispatch = useDispatch();
	const { designSystemContent, selectedCategories, activeTab, viewMode } = useSelector(
		(r: RootReducer) => ({
			designSystemContent: r.mode.designSystemContent,
			selectedCategories: r.mode.designSystemFilters.components.selectedCategories,
			activeTab: selectActiveDsTab(r.mode),
			viewMode: r.mode.dsViewPreferences.componentsViewMode ?? 'grid',
		}),
		shallowEqual
	);

	// Get contentPath from active tab
	const contentPath = activeTab?.contentPath;
	const scrollRef = useScrollMemory(activeTab?.id ?? null);

	// Get available categories with counts
	const availableCategories = useMemo(() => {
		if (!designSystemContent?.components) return [];
		return (Object.keys(categoryDisplayNames) as ComponentCategory[]).map((category) => ({
			value: category,
			label: categoryDisplayNames[category],
			count: designSystemContent.components!.byCategory[category]?.length || 0,
		}));
	}, [designSystemContent?.components]);

	// Filter components based on selected categories
	const componentsToDisplay = useMemo(() => {
		if (!designSystemContent?.components) return [];

		let components: DesignSystemComponent[] = [];

		// If categories selected, show union of all selected categories
		if (selectedCategories.length > 0) {
			const componentSets = selectedCategories.map(
				(cat) => designSystemContent.components!.byCategory[cat] || []
			);
			// Union: combine all sets and remove duplicates
			const componentMap = new Map<string, DesignSystemComponent>();
			componentSets.forEach((componentSet) => {
				componentSet.forEach((component) => {
					componentMap.set(component.id, component);
				});
			});
			components = Array.from(componentMap.values());
		} else {
			// No categories selected, show all
			components = designSystemContent.components.all || [];
		}

		return components;
	}, [designSystemContent?.components, selectedCategories]);

	const rawCode = designSystemContent?.rawCode || {
		theme: '',
		styles: '',
		componentsCss: '',
		componentsJs: '',
	};

	// Compute dependency counts per component for the list view
	const depsByComponent = useMemo(() => {
		const graph = designSystemContent?.graph;
		if (!graph) return new Map<string, { total: number; broken: number }>();
		const defined = new Set(Object.keys(graph.symbols));
		const result = new Map<string, { total: number; broken: number }>();
		for (const comp of componentsToDisplay) {
			const deps = [...new Set(graph.uses[comp.tagName] || [])];
			const broken = deps.filter((d) => !defined.has(d)).length;
			result.set(comp.id, { total: deps.length, broken });
		}
		return result;
	}, [designSystemContent?.graph, componentsToDisplay]);

	// Check if contentPath points to a specific component (detail view)
	const detailComponent = useMemo(() => {
		if (contentPath && designSystemContent?.components?.byId) {
			return designSystemContent.components.byId[contentPath];
		}
		return null;
	}, [contentPath, designSystemContent?.components?.byId]);

	// Navigate to component page on card click
	const onCardClick = (component: DesignSystemComponent) => {
		const tabId = buildTabId('components', component.id);
		dispatch(
			addDsTab({
				id: tabId,
				kind: 'components',
				contentPath: component.id,
				label: component.name,
				showBackButton: true,
			})
		);
		dispatch(setActiveDsTab(tabId));
	};

	// Render detail view if contentPath points to a specific component
	if (detailComponent) {
		return (
			<ComponentPage
				component={detailComponent}
				rawCode={rawCode}
				showBackButton={activeTab?.showBackButton}
			/>
		);
	}

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderViewToggle = () => (
		<ViewToggle
			options={[
				{ value: 'grid', icon: IconType.GRID, title: 'Grid view' },
				{ value: 'list', icon: IconType.STACK, title: 'List view' },
			]}
			value={viewMode}
			onChange={(v) => dispatch(setDsViewPreference({ componentsViewMode: v as 'grid' | 'list' }))}
		/>
	);

	const renderGrid = () => (
		<S.ComponentGrid>
			{componentsToDisplay.map((component) => (
				<ComponentCard
					key={component.id}
					component={component}
					onClick={() => onCardClick(component)}
					rawCode={rawCode}
				/>
			))}
		</S.ComponentGrid>
	);

	const renderList = () => (
		<S.ComponentList>
			{componentsToDisplay.map((component) => {
				const deps = depsByComponent.get(component.id);
				return (
					<S.ComponentRow key={component.id} onClick={() => onCardClick(component)}>
						<S.RowName>{component.name}</S.RowName>
						<S.RowTagName>&lt;{component.tagName}&gt;</S.RowTagName>
						<S.RowCategory>{categoryDisplayNames[component.category]}</S.RowCategory>
					<S.RowProps>
						{component.props.length} prop
						{component.props.length !== 1 ? 's' : ''}
					</S.RowProps>
					<S.RowDeps>
						{deps && deps.total > 0 && (
							<>
								{deps.total} dependenc{deps.total !== 1 ? 'ies' : 'y'}
								{deps.broken > 0 && <S.BrokenBadge>{deps.broken}</S.BrokenBadge>}
							</>
						)}
					</S.RowDeps>
					</S.ComponentRow>
				);
			})}
		</S.ComponentList>
	);

	return (
		<Base.SectionBase ref={scrollRef}>
			<Base.SectionHeader>
				<Base.SectionTitle>Components</Base.SectionTitle>
				{componentsToDisplay.length > 0 && renderViewToggle()}
			</Base.SectionHeader>
			<Base.SectionContent>
				<FilterPills section="components" categories={availableCategories} />
				{componentsToDisplay.length === 0 ? (
					<p>
						No components found
						{selectedCategories.length > 0 && ` matching selected categories`}.
					</p>
				) : viewMode === 'list' ? (
					renderList()
				) : (
					renderGrid()
				)}
			</Base.SectionContent>
		</Base.SectionBase>
	);
};

export default ComponentsView;
